import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import SEO from '../components/SEO';
import { Search, Star, Clock, MapPin, Filter, X, Truck, Package, UtensilsCrossed } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export default function RestaurantsPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { formatPrice } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [cuisineFilter, setCuisineFilter] = useState(searchParams.get('cuisine') || '');
  // Start with null if no service param - force user to choose
  const [serviceType, setServiceType] = useState(searchParams.get('service') || null);
  const [showFilters, setShowFilters] = useState(false);
  const [showServiceSelector, setShowServiceSelector] = useState(!searchParams.get('service'));

  const cuisines = ['Italian', 'Chinese', 'Mexican', 'Indian', 'Japanese', 'American', 'Thai', 'Mediterranean'];

  const serviceTypes = [
    { id: 'delivery', labelKey: 'orderType.delivery', icon: Truck, descKey: 'orderType.deliveryDesc' },
    { id: 'takeaway', labelKey: 'orderType.takeaway', icon: Package, descKey: 'orderType.takeawayDesc' },
    { id: 'dine_in', labelKey: 'orderType.dineIn', icon: UtensilsCrossed, descKey: 'orderType.dineInDesc' },
  ];

  const fetchRestaurants = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/restaurants';
      const params = new URLSearchParams();
      if (cuisineFilter) params.append('cuisine', cuisineFilter);
      // Service type filtering is now done server-side
      if (serviceType) params.append('service_type', serviceType);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      setRestaurants(response.data);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    } finally {
      setLoading(false);
    }
  }, [cuisineFilter, serviceType, api]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  // Client-side filtering only for search query (service type is filtered server-side)
  const getFilteredRestaurants = () => {
    let filtered = restaurants;
    
    // Filter by search query only
    if (searchQuery) {
      filtered = filtered.filter(restaurant =>
        restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        restaurant.cuisine_type?.some(c => c.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    return filtered;
  };

  const filteredRestaurants = getFilteredRestaurants();

  const handleServiceTypeChange = (type) => {
    setServiceType(type);
    setShowServiceSelector(false);
    setSearchParams(prev => {
      prev.set('service', type);
      return prev;
    });
  };

  const clearFilters = () => {
    setCuisineFilter('');
    setSearchQuery('');
    // Don't clear service type - keep the selection
    setSearchParams(prev => {
      prev.delete('cuisine');
      return prev;
    });
  };

  return (
    <>
      <SEO page="restaurants" />
      
      {/* Force Service Type Selection Modal */}
      {showServiceSelector && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-8 shadow-2xl animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                {t('restaurants.howToOrder')}
              </h2>
              <p className="text-slate-500">
                {t('restaurants.selectServiceType') || 'Please select how you would like to order'}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              {serviceTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => handleServiceTypeChange(type.id)}
                    className="group p-6 rounded-2xl border-2 border-slate-200 hover:border-orange-500 hover:bg-orange-50 transition-all text-center"
                    data-testid={`select-service-${type.id}`}
                  >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                      <Icon className="w-8 h-8 text-orange-500 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-bold text-lg text-slate-900 mb-1">{t(type.labelKey)}</h3>
                    <p className="text-sm text-slate-500">{t(type.descKey)}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-slate-50 pt-20">
        {/* Service Type Selector - Always visible for changing selection */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h2 className="text-lg font-medium mb-4 text-center">{t('restaurants.howToOrder')}</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {serviceTypes.map((type) => {
                const Icon = type.icon;
                const isActive = serviceType === type.id;
              return (
                <button
                  key={type.id}
                  onClick={() => handleServiceTypeChange(type.id)}
                  className={`flex items-center gap-3 px-6 py-3 rounded-xl transition-all ${
                    isActive 
                      ? 'bg-white text-orange-600 shadow-lg scale-105' 
                      : 'bg-white/20 hover:bg-white/30 text-white'
                  }`}
                  data-testid={`service-type-${type.id}`}
                >
                  <Icon className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-semibold">{t(type.labelKey)}</div>
                    <div className={`text-xs ${isActive ? 'text-orange-500' : 'text-white/80'}`}>
                      {t(type.descKey)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search Header */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder={t('search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
                data-testid="search-input"
              />
            </div>

            {/* Cuisine filter pills */}
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
              <Button 
                variant={!cuisineFilter ? "default" : "outline"} 
                size="sm"
                onClick={() => {
                  setCuisineFilter('');
                  setSearchParams(prev => {
                    prev.delete('cuisine');
                    return prev;
                  });
                }}
              >
                {t('restaurants.allCuisines')}
              </Button>
              {cuisines.slice(0, 4).map((cuisine) => (
                <Button
                  key={cuisine}
                  variant={cuisineFilter === cuisine ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setCuisineFilter(cuisine);
                    setSearchParams(prev => {
                      prev.set('cuisine', cuisine);
                      return prev;
                    });
                  }}
                >
                  {cuisine}
                </Button>
              ))}
            </div>

            {/* Clear filters */}
            {(cuisineFilter || searchQuery || serviceType !== 'delivery') && (
              <Button variant="ghost" onClick={clearFilters} className="text-slate-500" data-testid="clear-filters">
                <X className="w-4 h-4 mr-1" /> {t('restaurants.clearFilters')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900">
            {serviceType === 'delivery' && t('restaurants.forDelivery')}
            {serviceType === 'takeaway' && t('restaurants.forTakeaway')}
            {serviceType === 'dine_in' && t('restaurants.forDineIn')}
            {cuisineFilter && ` - ${cuisineFilter}`}
          </h1>
          <span className="text-slate-500">{filteredRestaurants.length} {t('restaurants.count')}</span>
        </div>

        {filteredRestaurants.length === 0 && !loading && (
          <div className="text-center py-12 bg-white rounded-2xl">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {serviceType === 'delivery' && <Truck className="w-8 h-8 text-slate-400" />}
              {serviceType === 'takeaway' && <Package className="w-8 h-8 text-slate-400" />}
              {serviceType === 'dine_in' && <UtensilsCrossed className="w-8 h-8 text-slate-400" />}
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {t('restaurants.noResults')}
            </h3>
            <p className="text-slate-500 mb-4">
              {serviceType === 'delivery' && t('noResults.delivery')}
              {serviceType === 'takeaway' && t('noResults.takeaway')}
              {serviceType === 'dine_in' && t('noResults.dineIn')}
            </p>
            <Button onClick={() => handleServiceTypeChange('delivery')}>
              {t('restaurants.tryDelivery')}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                <div className="w-full h-48 bg-slate-200 rounded-xl mb-4"></div>
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredRestaurants.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRestaurants.map((restaurant, index) => (
              <Link
                key={restaurant.id}
                to={`/restaurant/${restaurant.id}?order_type=${serviceType}`}
                className="card-restaurant animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`restaurant-card-${restaurant.id}`}
              >
                <div className="relative">
                  <img
                    src={restaurant.image_url || `https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=250&fit=crop`}
                    alt={restaurant.name}
                    className="w-full h-48 object-cover"
                  />
                  {restaurant.delivery_enabled ? (
                    <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                      {t('restaurants.deliveryAvailable')}
                    </span>
                  ) : (
                    <span className="absolute top-3 left-3 bg-slate-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                      {t('restaurants.pickupOnly')}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg text-slate-900 mb-1">{restaurant.name}</h3>
                  <p className="text-sm text-slate-500 mb-2">{restaurant.cuisine_type?.join(', ') || 'Various'}</p>
                  <p className="text-sm text-slate-400 mb-3 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {restaurant.city}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="font-medium text-slate-700">{restaurant.rating?.toFixed(1) || '4.5'}</span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-500">
                      <Clock className="w-4 h-4" />
                      <span>25-35 min</span>
                    </div>
                    <span className="text-slate-500">
                      {t('restaurants.min')} £{restaurant.minimum_order || 10}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <Search className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">{t('restaurants.noResults')}</h2>
            <p className="text-slate-500 mb-6">{t('restaurants.tryAdjusting')}</p>
            <Button onClick={clearFilters} className="btn-primary" data-testid="clear-all-filters">
              {t('restaurants.clearAll')}
            </Button>
          </div>
        )}
        </div>
      </div>
    </>
  );
}
